<template>
    <v-dialog v-model="dialog" max-width="1000" :lazy="true" scrollable persistent id="measure-editor">
        <v-card>
            <v-card-title class="headline">{{ title }}</v-card-title>
            
            <v-card-text>
                <v-container grid-list-xs class="pa-0">
                    <v-layout row nowrap>
                        <v-flex md3>
                            <div class="measure-container">
                                <measure :style="{ height: '80px' }" :columnsMax="1" :isEditable="false" ref="Measure"
                                         :partIndex="-1" :measureIndex="-1" :width="170" :height="80" :measure="measure"/>
                            </div>

                            <div class="my-4">
                                <v-checkbox :label="$t('repeat-measure') + ' (%)'" :value="selectedRepeat"
                                            @click.native="toggleRepeatMeasure()"
                                            class="ma-2" color="primary" dark hide-details></v-checkbox>
                                <v-checkbox :label="$t('open-repeat')" :value="measure.openRepeat"
                                            @click.native="toggleOpenRepeat('open')"
                                            class="ma-2" color="primary" dark hide-details></v-checkbox>
                                <v-checkbox :label="$t('close-repeat')" :value="measure.closeRepeat"
                                            @click.native="toggleOpenRepeat('close')"
                                            class="ma-2" color="primary" dark hide-details></v-checkbox>
                                <v-select :items="numberRepeatsList" v-model="measure.numberRepeats" v-if="measure.closeRepeat"
                                          :label="$t('measure-number-repeats')" class="pl-2 pr-4"></v-select>
                            </div>

                            <div class="my-3">
                                <v-btn @click.native="$refs.MeasureColorDialog.open()" color="success">
                                    <v-icon>format_color_fill</v-icon>&nbsp;
                                    {{ $t('color-measure') }}
                                </v-btn>
                            </div>

                            <div class="my-4">
                                <v-btn color="error" class="delete-chord" @click.stop="deleteMeasure()" v-if="!creation">
                                    <v-icon>delete</v-icon>&nbsp;
                                    {{ $t('delete-measure') }}
                                </v-btn>
                            </div>
                        </v-flex>

                        <v-flex md9>
                            <v-card flat>
                                <v-card-text class="pr-0">
                                    <div class="ma-1" style="text-align: left">
                                        <div class="property-container">
                                            <div class="label">{{ $t('time') }}</div>
                                            <div class="property">
                                                <v-btn v-for="measureTime in measureTimes" :key="measureTime"
                                                       class="small-button"
                                                       @click="selectProperty('measureTime', measureTime)"
                                                       :color="getPropertyColor('measureTime', measureTime)"
                                                       :disabled="selectedRepeat">
                                                    {{ measureTime }}
                                                </v-btn>
                                            </div>
                                        </div>

                                        <div>
                                            <v-divider class="my-3"></v-divider>

                                            <div class="subheading mb-3">{{ $t('chord-composition') }}</div>
                                            
                                            <div class="property-container">
                                                <div class="label">{{ $t('root') }}</div>
                                                <div class="property">
                                                    <v-btn v-for="root in roots" :key="root" class="small-button"
                                                        @click="selectProperty('root', root)"
                                                        :color="getPropertyColor('root', root)"
                                                        :disabled="selectedMeasureTimeBtn === 0 || selectedRepeat">
                                                        {{ root }}
                                                    </v-btn>
                                                    <div class="alteration-separator"></div>
                                                    <v-btn v-for="alteration in alterations" :key="alteration" class="small-button"
                                                        @click="selectProperty('alteration', alteration)"
                                                        :color="getPropertyColor('alteration', alteration)"
                                                        :disabled="selectedMeasureTimeBtn === 0 || selectedRepeat">
                                                        {{ alteration }}
                                                    </v-btn>
                                                </div>
                                            </div>

                                            <div class="property-container">
                                                <div class="label">{{ $t('formula') }}</div>
                                                <div class="property">
                                                    <v-btn v-for="formula in formulas" :key="formula" class="small-button"
                                                        @click="selectProperty('formula', formula)"
                                                        :color="getPropertyColor('formula', formula)"
                                                        :disabled="selectedMeasureTimeBtn === 0 || selectedRepeat">
                                                        {{ formula }}
                                                    </v-btn>
                                                </div>
                                            </div>

                                            <div class="property-container">
                                                <div class="label">{{ $t('bass') }}</div>
                                                <div class="property">
                                                    <v-btn v-for="bass in basses" :key="bass" class="small-button"
                                                        @click="selectProperty('bass', bass)"
                                                        :color="getPropertyColor('bass', bass)"
                                                        :disabled="selectedMeasureTimeBtn === 0 || selectedRepeat">
                                                        {{ bass }}
                                                    </v-btn>
                                                    <div class="alteration-separator"></div>
                                                    <v-btn v-for="bassAlteration in bassAlterations" :key="bassAlteration" class="small-button"
                                                        @click="selectProperty('bassAlteration', bassAlteration)"
                                                        :color="getPropertyColor('bassAlteration', bassAlteration)"
                                                        :disabled="selectedMeasureTimeBtn === 0 || selectedRepeat">
                                                        {{ bassAlteration }}
                                                    </v-btn>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-flex>
                    </v-layout>
                </v-container>
            </v-card-text>
            
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn flat @click.native="close()">{{ $t('cancel') }}</v-btn>
                <v-btn color="primary" flat @click.native="valid()" :disabled="!isMeasureValid">
                    {{ $t('valid') }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <measure-color-dialog ref="MeasureColorDialog" v-on:selectColor="updateColor($event)"></measure-color-dialog>
    </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import colors from 'vuetify/es5/util/colors';
import store from '@/stores';
import Measure from '@/components/Measure.vue';
import Chord from '@/models/Chord';
import MeasureModel from '@/models/Measure';
import * as types from '@/stores/types';
import MeasureColorDialog from '@/components/dialogs/MeasureColorDialog.vue';

export default {
    name:  'measure-editor-dialog',
    store: store,
    data:  function()
    {
        return {
            selectedMeasureTimeBtn:    0,
            selectedRootBtn:           '',
            selectedAlterationBtn:     '',
            selectedFormulaBtn:        '',
            selectedBassBtn:           '',
            selectedBassAlterationBtn: '',
            selectedRepeat:            false,
            dialog:                    false,
            creation:                  false,
            partIndex:                 -1,
            measureIndex:              -1,
            measure:                   MeasureModel.getEmptyMeasure(),
        };
    },
    components: {
        Measure,
        MeasureColorDialog,
    },
    mounted:  function()
    {
        this.init();
    },
    updated: function()
    {
        this.measure = this.measureSelected;

        if (this.selectedMeasureTimeBtn !== 0)
        {
            this.selectedRootBtn = this.measure.chords[this.selectedMeasureTimeBtn - 1].root;
            this.selectedAlterationBtn = this.measure.chords[this.selectedMeasureTimeBtn - 1].alteration;
            this.selectedFormulaBtn = this.measure.chords[this.selectedMeasureTimeBtn - 1].formula;
            this.selectedBassBtn = this.measure.chords[this.selectedMeasureTimeBtn - 1].bass;
            this.selectedBassAlterationBtn = this.measure.chords[this.selectedMeasureTimeBtn - 1].bassAlteration;
            this.selectedRepeat = MeasureModel.isRepeatedMeasure(this.measure);
        }
    },
    computed: {
        ...mapGetters({
            song: 'song',
        }),
        measureTimes:    () => Chord.getMeasureTimes(),
        roots:           () => Chord.getRoots(),
        alterations:     () => Chord.getAlterations(),
        formulas:        () => Chord.getFormulas(),
        basses:          () => Chord.getBasses(),
        bassAlterations: () => Chord.getBassAlterations(),
        // Titre
        title:           function()
        {
            return (this.creation) ? this.$t('add-measure') : this.$t('edit-measure');
        },
        // Partie sélectionnée
        partSelected: function()
        {
            return (this.song.parts[this.partIndex] !== undefined)
                ? this.song.parts[this.partIndex]
                : null;
        },
        // Mesure sélectionnée
        measureSelected: function()
        {
            const emptyMeasure = MeasureModel.getEmptyMeasure();

            // On utilise la variable dialog pour faire en sorte de mettre à jour measureSelected à l'ouverture de la boîte de dialogue
            if (this.dialog)
            {
                return (this.partSelected !== null && this.partSelected.measures[this.measureIndex] !== undefined)
                    ? { ...this.partSelected.measures[this.measureIndex] } // Pour cloner le tableau et ne pas modifier le store sans mutation
                    : emptyMeasure;
            }
            else
            {
                return emptyMeasure;
            }
        },
        // La mesure est-elle valide ?
        isMeasureValid: function()
        {
            return MeasureModel.isValid(this.measure);
        },
        // Tableau du nombre de répétitions
        numberRepeatsList: function()
        {
            return [2].concat(Array.from(new Array(98), (val, index) => index + 2));
        },
    },
    methods: {
        ...mapActions({
            editMeasureAction:   types.A_SONG_EDIT_MEASURE,
            deleteMeasureAction: types.A_SONG_DELETE_MEASURE,
        }),
        /**
         * Mise à jour de la couleur de la mesure
         *
         * @author Fabien Bellanger
         * @param {String} color Couleur
         */
        updateColor: function(color)
        {
            this.$set(this.measure, 'color', color);

            this.$refs.Measure.redraw();
        },
        /**
         * Ouverture de la boîte de dialogue
         *
         * @author Fabien Bellanger
         * @param {Number} partIndex            Indice de la partie
         * @param {Number} measureIndex         Indice de la mesure
         * @param {Number} isMeasureCreation    Est-ce une création ?
         */
        open: function(partIndex, measureIndex, isMeasureCreation)
        {
            this.dialog = true;
            this.partIndex = partIndex;
            this.measureIndex = measureIndex;
            this.creation = isMeasureCreation;
        },
        /**
         * Initialisation
         *
         * @author Fabien Bellanger
         */
        init: function()
        {
            this.selectedMeasureTimeBtn = 1;

            this.resetProperties();

            // On remet la scrollbar en haut
            // -----------------------------
            const vCardText = document.getElementsByClassName('v-card__text');

            if (vCardText !== undefined && vCardText[0] !== undefined)
            {
                vCardText[0].scrollTop = 0;
            }
        },
        /**
         * Initialisation de propriétés
         *
         * @author Fabien Bellanger
         */
        resetProperties: function()
        {
            this.selectedRootBtn = '';
            this.selectedAlterationBtn = '';
            this.selectedFormulaBtn = '';
            this.selectedBassBtn = '';
            this.selectedBassAlterationBtn = '';
            this.selectedRepeat = false;

            this.measure = MeasureModel.getEmptyMeasure();
        },
        /**
         * Répétition de la mesure
         *
         * @author Fabien Bellanger
         */
        toggleRepeatMeasure: function()
        {
            const measure = MeasureModel.getEmptyMeasure();
            
            if (!this.selectedRepeat)
            {
                // La mesure devient une répétition
                // --------------------------------
                measure.chords[0].root = '%';
            }

            // Pour que la mise à jour soit "réactive"
            this.$set(this.measure, 'chords', measure.chords);

            this.$refs.Measure.redraw();
        },
        /**
         * Fermeture de la boîte de dialogue
         *
         * @author Fabien Bellanger
         */
        close: function()
        {
            this.dialog = false;
            this.init();
        },
        /**
         * Valider
         *
         * @author Fabien Bellanger
         */
        valid: function()
        {
            if (MeasureModel.isValid(this.measure))
            {
                this.editMeasureAction({
                    partIndex:    this.partIndex,
                    measureIndex: this.measureIndex,
                    measure:      this.measure,
                });

                this.close();
            }
            else
            {
                // this.snackbar = true;
            }
        },
        /**
         * Sélection d'une propriété
         *
         * @author Fabien Bellanger
         * @param {String} property Propriété
         * @param {String} value    Valeur
         */
        selectProperty: function(property, value)
        {
            const propertyName = 'selected' + property.charAt(0).toUpperCase() + property.slice(1) + 'Btn';

            // Si on change le temps, on remet à zéro les autres proriétés
            // -----------------------------------------------------------
            if (property === 'measureTime')
            {
                this.resetProperties();
            }
            
            if (this[propertyName] === value)
            {
                this[propertyName] = (property === 'measureTime') ? 0 : '';

                // Si aucune fondamentale sélectionnée, on désélectionne les autres propriétés
                // ---------------------------------------------------------------------------
                if (property === 'root')
                {
                    this.selectedAlterationBtn = '';
                    this.selectedFormulaBtn = '';
                    this.selectedBassBtn = '';
                    this.selectedBassAlterationBtn = '';
                }
            }
            else
            {
                this[propertyName] = value;
            }

            // Mise à jour de l'accord
            // -----------------------
            this.updateChord();
        },
        /**
         * Couleur d'une propriété
         *
         * @author Fabien Bellanger
         * @param {String} property Propriété
         * @param {String} value    Valeur
         */
        getPropertyColor: function(property, value)
        {
            const propertyName = 'selected' + property.charAt(0).toUpperCase() + property.slice(1) + 'Btn';
            
            if (this[propertyName] === value && this[propertyName] !== '')
            {
                // La propriété est sélectionnée
                // -----------------------------
                return 'primary';
            }
            else
            {
                if (property === 'measureTime' && this.measure.chords[value - 1].root !== '')
                {
                    // Si un temps possède un accord et qu'il n'est pas sélectionné, on change sa couleur
                    // ----------------------------------------------------------------------------------
                    return colors.green.base;
                }
                else
                {
                    // La propriété n'est pas sélectionnée
                    // -----------------------------------
                    return '';
                }
            }
        },
        toggleOpenRepeat: function(property)
        {
            const propertyName = property + 'Repeat';

            this.$set(this.measure, propertyName, !this.measure[propertyName]);

            this.$refs.Measure.redraw();
        },
        /**
         * Mise à jour d'un accord
         *
         * @author Fabien Bellanger
         */
        updateChord: function()
        {
            const chord = {
                root:           this.selectedRootBtn,
                alteration:     this.selectedAlterationBtn,
                formula:        this.selectedFormulaBtn,
                bass:           this.selectedBassBtn,
                bassAlteration: this.selectedBassAlterationBtn,
            };

            let chords = [...this.measure.chords];
            chords[this.selectedMeasureTimeBtn - 1] = chord;

            // Pour que la mise à jour soit "réactive"
            this.$set(this.measure, 'chords', chords);

            this.$refs.Measure.redraw();
        },
        /**
         * Suppression de la mesure
         *
         * @author Fabien Bellanger
         */
        deleteMeasure: function()
        {
            this.deleteMeasureAction({
                partIndex:    this.partIndex,
                measureIndex: this.measureIndex,
            });

            this.close();
        },
    },
};
</script>

<style scoped lang="scss">
    .v-btn {
        text-transform: none !important;
    }

    .small-button {
        min-width:     50px !important;
        margin:        2px;
        padding-right: 12px !important;
        padding-left:  12px !important;
    }

    .measure-container {
        width:  170px;
        margin: 12px auto 0px auto;
    }

    .property-container {
        display:       flex;
        margin-bottom: 12px;

        .label {
            display:       inline-block;
            flex:          0 0 auto;
            width:         100px;
            margin-top:    9px;
            text-align:    right;
            padding-right: 10px;
        }

        .alteration-separator {
            width: 60px;
        }

        .property {
            display:   flex;
            flex-flow: row wrap;
        }
    }

    .delete-chord {
        display: inline-block;
    }
</style>
